import { HttpClient } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'
import { StorageService } from '@everyday-speech/eds-design-system-lib'
import { BehaviorSubject, Observable, tap } from 'rxjs'
import { StorageKeys } from '@app/common/types/storage-keys'
import { environment } from '@env/environment'

interface SsoLoginResponse {
  sessionToken: string
  // slp-user-api mints a v3 (Cognito-backed) admin session for SSO; platform-access
  // selects its token-validation path from the `x-auth-version` header, so we must
  // persist and replay this version on authenticated requests.
  authVersion?: string
}

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  authTokenStorageKey = StorageKeys.AUTH_TOKEN_STORAGE_KEY
  activeUserStorageKey = StorageKeys.ACTIVE_USER_STORAGE_KEY
  authVersionStorageKey = StorageKeys.AUTH_VERSION_STORAGE_KEY

  private readonly storageService: StorageService = inject(StorageService)
  private readonly http: HttpClient = inject(HttpClient)

  identityToken$ = new BehaviorSubject<string | undefined>(this.identityToken)
  isAuthenticated$ = new BehaviorSubject<boolean>(!!this.identityToken)

  constructor() {
    this.identityToken$.subscribe(token => {
      this.isAuthenticated$.next(!!token)
    })
  }

  private get identityToken() {
    return this.storageService.read<string>(this.authTokenStorageKey)
  }

  private set identityToken(token: string | undefined) {
    this.storageService.put(this.authTokenStorageKey, token)
    if (!token) {
      this.identityToken$.next(undefined)
    } else {
      this.identityToken$.next(token)
    }
  }

  /**
   * The auth version of the stored session, replayed as `x-auth-version` so
   * platform-access validates the token with the matching strategy. Undefined for
   * the legacy `?token=` handoff, which has no version and rides the backend default.
   */
  get authVersion(): string | undefined {
    return this.storageService.read<string>(this.authVersionStorageKey)
  }

  authenticateWithToken(token: string, authVersion?: string) {
    // Keep the stored version consistent with the token being set: persist it for
    // versioned (SSO/v3) sessions, and clear any prior value for the legacy `?token=`
    // handoff — otherwise a stale version would replay a wrong `x-auth-version` and
    // make platform-access validate the new token with the wrong strategy.
    if (authVersion) {
      this.storageService.put(this.authVersionStorageKey, authVersion)
    } else {
      this.storageService.remove(this.authVersionStorageKey)
    }
    this.identityToken = token
  }

  clearAuthentication() {
    this.storageService.remove(this.authTokenStorageKey)
    this.storageService.remove(this.activeUserStorageKey)
    this.storageService.remove(this.authVersionStorageKey)
    this.identityToken = undefined
  }

  /**
   * Builds the Cognito Hosted UI `/oauth2/authorize` URL that starts the SSO login.
   * `state` is an opaque CSRF token the caller stores and re-validates on the callback.
   */
  buildHostedUiUrl(state: string): string {
    const params = new URLSearchParams({
      client_id: environment.COGNITO_ADMIN_CLIENT_ID,
      response_type: 'code',
      scope: environment.COGNITO_SCOPES,
      redirect_uri: environment.COGNITO_REDIRECT_URI,
      state,
    })
    return `${environment.COGNITO_HOSTED_UI_DOMAIN}/oauth2/authorize?${params.toString()}`
  }

  /**
   * Exchanges the OAuth `code` returned by the Hosted UI for an app session token
   * (server-side exchange via platform-access-service) and stores it through the
   * existing `authenticateWithToken` path. `/auth/login/sso` is unauthenticated, so
   * this bypasses PlatformAccessService (which would inject an Authorization header
   * and create a circular dependency).
   */
  loginWithCode(code: string): Observable<SsoLoginResponse> {
    return this.http
      .post<SsoLoginResponse>(`${environment.PLATFORM_ACCESS_BASE_URL}/auth/login/sso`, { code })
      .pipe(tap(response => this.authenticateWithToken(response.sessionToken, response.authVersion)))
  }

  logout() {
    this.clearAuthentication()
    const params = new URLSearchParams({
      client_id: environment.COGNITO_ADMIN_CLIENT_ID,
      logout_uri: environment.COGNITO_LOGOUT_URI,
    })
    this.redirect(`${environment.COGNITO_HOSTED_UI_DOMAIN}/logout?${params.toString()}`)
  }

  /** Navigation seam — isolated so specs can assert redirects without leaving the test page. */
  redirect(url: string): void {
    window.location.href = url
  }
}
